import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const DEMO_MAILTO = "mailto:hello@getministryhub.com?subject=Request%20a%20Demo";

const PLANS = [
  {
    name: "Connect",
    tagline: "Start welcoming people well.",
    monthly: 50,
    annual: 500,
    trial: true,
    available: true,
    description: "For churches ready to create intentional first impressions and clear next steps.",
    support: "Everything you need to launch a beautiful, welcoming MinistryHub experience.",
    features: ["Welcome Kiosk", "Unlimited guests", "Connect Cards", "Responses", "Experiences", "Basic Insights", "Church branding", "QR codes", "Email notifications"],
    cta: "Get Started",
  },
  {
    name: "Grow",
    tagline: "Build intentional pathways.",
    monthly: 100,
    annual: 1000,
    trial: false,
    available: false,
    description: "For churches ready to move beyond collecting information and begin intentionally guiding people toward discipleship.",
    support: "Everything in Connect, plus tools that help your team follow up, organize next steps, and build clearer discipleship pathways.",
    features: ["Everything in Connect", "Automated follow-up", "Custom email responses", "Team management", "Advanced Insights", "Integrations", "API access", "Custom branding", "Ministry reports"],
    cta: "Coming Soon",
  },
  {
    name: "Multiply",
    tagline: "Lead with clarity.",
    monthly: 200,
    annual: 2000,
    trial: false,
    available: false,
    description: "For churches leading multiple ministries or campuses who want one place to understand ministry health and remove barriers across the church.",
    support: "Everything in Grow, plus ministry-wide visibility, advanced analytics, and support for larger teams.",
    features: ["Everything in Grow", "Multi-campus", "Executive dashboards", "Cross-platform analytics", "Giving analytics", "Attendance analytics", "Volunteer analytics", "Livestream analytics", "AI ministry insights", "Priority support", "Early access"],
    cta: "Coming Soon",
  },
];

const WHICH = [
  { name: "Connect", quote: "We're creating a better first impression." },
  { name: "Grow", quote: "We're intentionally helping people take their next steps." },
  { name: "Multiply", quote: "We want a complete picture of ministry across our church." },
];

const EVERY = ["Unlimited people", "Friendly support", "Secure hosting", "Continuous improvements", "Built by church leaders", "Designed with hospitality in mind"];

const FAQS = [
  { q: "Can I change plans later?", a: "Absolutely. As your church grows, MinistryHub grows with you." },
  { q: "Is there a contract?", a: "No. Stay because it helps your ministry, not because you're locked in." },
  { q: "Do I need to switch church software?", a: "No. MinistryHub is designed to work alongside the tools your church already uses." },
  { q: "Is there a free trial?", a: "Yes. New churches get one 14-day free trial on MinistryHub Connect, so your team can experience it before you're billed. After the trial, Connect continues automatically at your selected plan." },
  { q: "Will you help us get set up?", a: "Absolutely. Helping churches succeed is part of the mission." },
  { q: "Why isn't everything included in one plan?", a: "Because every church is different. We want to make MinistryHub accessible for churches of every size while continuing to invest in features that help larger ministries thrive." },
];

const Section = ({ children, className = "" }) => (
  <section className={`px-6 md:px-12 lg:px-20 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

export default function Pricing() {
  const [interval, setInterval] = useState("monthly");
  const annual = interval === "annual";

  return (
    <div className="min-h-screen bg-[var(--mh-soft-white)] text-[var(--mh-charcoal)] antialiased">
      {/* Nav */}
      <nav className="px-6 md:px-12 lg:px-20 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="pricing-logo">
            <Logo theme="black" height={30} />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-[var(--mh-muted-text)] hover:text-[var(--mh-charcoal)] hidden sm:inline px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/signup" data-testid="pricing-nav-cta">
              <Button className="bg-[var(--mh-charcoal)] hover:bg-black text-white rounded-full px-5 h-10 transition-colors">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <Section className="pt-14 md:pt-20 pb-8">
        <div className="max-w-3xl fade-up">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1]">Every church starts somewhere.</h1>
          <p className="mt-6 text-lg md:text-xl text-[var(--mh-muted-text)] leading-relaxed">
            Whether you're creating a better first impression, building intentional next steps, or leading multiple campuses, MinistryHub grows with your church.
          </p>
          <p className="mt-3 text-[var(--mh-muted-text)]">Connect → Grow → Multiply. Find the plan that fits where your church is today.</p>
        </div>
      </Section>

      {/* Interval toggle */}
      <Section className="pb-2">
        <div className="inline-flex items-center rounded-full bg-white border border-[var(--mh-border)] p-1 mh-shadow-soft" data-testid="pricing-interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            className={`px-6 h-10 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-[var(--mh-charcoal)] text-white" : "text-[var(--mh-muted-text)]"}`}
            data-testid="pricing-interval-monthly"
          >Monthly</button>
          <button
            onClick={() => setInterval("annual")}
            className={`px-6 h-10 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${annual ? "bg-[var(--mh-charcoal)] text-white" : "text-[var(--mh-muted-text)]"}`}
            data-testid="pricing-interval-annual"
          >Annual <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--mh-green)] text-white">Save 2 months</span></button>
        </div>
      </Section>

      {/* Plans */}
      <Section className="py-8 md:py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div key={p.name} className={`flex flex-col p-8 rounded-[var(--mh-radius)] bg-white border mh-shadow-card ${p.available ? "border-[var(--mh-border)]" : "border-dashed border-[var(--mh-border)]"}`} data-testid={`plan-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold tracking-tight">{p.name}</h2>
                {p.available ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--mh-green)] text-white font-medium" data-testid={`plan-${p.name.toLowerCase()}-badge`}>Available now</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--mh-surface)] text-[var(--mh-muted-text)] font-medium border border-[var(--mh-border)]" data-testid={`plan-${p.name.toLowerCase()}-badge`}>Coming Soon</span>
                )}
              </div>
              <p className="mt-1 text-[var(--mh-gold)] font-medium">{p.tagline}</p>
              <p className="mt-5">
                <span className="text-4xl font-bold text-[var(--mh-charcoal)]" data-testid={`plan-${p.name.toLowerCase()}-price`}>${annual ? p.annual.toLocaleString() : p.monthly}</span>
                <span className="text-[var(--mh-muted-text)]"> /{annual ? "year" : "month"}</span>
              </p>
              {p.available && p.trial && <p className="mt-1 text-sm text-[var(--mh-muted-text)]">Includes a 14-day free trial</p>}
              {!p.available && <p className="mt-1 text-sm text-[var(--mh-muted-text)]">Not yet available — launching soon</p>}
              <p className="mt-4 text-sm text-[var(--mh-muted-text)] leading-relaxed">{p.description}</p>
              <p className="mt-3 text-sm text-[var(--mh-charcoal)] leading-relaxed">{p.support}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 text-[var(--mh-green)] shrink-0" />
                    <span className={f.startsWith("Everything in") ? "font-medium text-[var(--mh-charcoal)]" : "text-[var(--mh-muted-text)]"}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.available ? (
                <Link to={`/signup?plan=${p.name.toLowerCase()}&interval=${interval}`} className="mt-8" data-testid={`plan-${p.name.toLowerCase()}-cta`}>
                  <Button className="w-full h-11 rounded-full bg-[var(--mh-charcoal)] hover:bg-black text-white transition-colors">{p.cta}</Button>
                </Link>
              ) : (
                <Button disabled className="mt-8 w-full h-11 rounded-full bg-[var(--mh-surface)] text-[var(--mh-muted-text)] border border-[var(--mh-border)] cursor-not-allowed" data-testid={`plan-${p.name.toLowerCase()}-cta-disabled`}>Coming Soon</Button>
              )}
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-[var(--mh-muted-text)]">Straightforward pricing with no surprises — cancel anytime.</p>
      </Section>

      {/* Which plan */}
      <Section className="py-16 md:py-20 bg-white border-y border-[var(--mh-border)]">
        <h2 className="text-3xl font-bold tracking-tight">Which plan is right for us?</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {WHICH.map((w) => (
            <div key={w.name} className="p-7 rounded-[var(--mh-radius)] bg-[var(--mh-soft-white)] border border-[var(--mh-border)]">
              <p className="text-sm uppercase tracking-[0.2em] text-[var(--mh-gold)] font-medium">{w.name}</p>
              <p className="mt-3 text-lg text-[var(--mh-charcoal)] leading-relaxed">&ldquo;{w.quote}&rdquo;</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Every plan includes */}
      <Section className="py-16 md:py-20">
        <h2 className="text-3xl font-bold tracking-tight">Every plan includes</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {EVERY.map((e) => (
            <div key={e} className="flex items-center gap-3 rounded-2xl bg-white border border-[var(--mh-border)] px-5 py-4 mh-shadow-soft">
              <Check className="w-4 h-4 text-[var(--mh-green)] shrink-0" />
              <span className="text-[var(--mh-charcoal)]">{e}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16 md:py-20 bg-[var(--mh-surface)]">
        <h2 className="text-3xl font-bold tracking-tight">Questions churches ask.</h2>
        <div className="mt-8 max-w-3xl space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl bg-white border border-[var(--mh-border)] p-5" data-testid={`faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-[var(--mh-charcoal)]">
                {f.q}
                <span className="text-[var(--mh-muted-text)] transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-[var(--mh-muted-text)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-20 md:py-24 bg-[var(--mh-charcoal)] text-white">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Still not sure?</h2>
          <p className="mt-5 text-lg text-white/75 leading-relaxed">Let's talk. We'll help you find the right fit, not the most expensive one.</p>
          <p className="mt-3 text-white/60 leading-relaxed">Our goal isn't to sell software. It's to help your church remove barriers so people can take their next step with Jesus.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={DEMO_MAILTO} data-testid="pricing-final-demo">
              <Button className="bg-white text-[var(--mh-charcoal)] hover:bg-[var(--mh-surface)] h-12 px-7 rounded-full text-base transition-colors">
                Request a Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/signup" data-testid="pricing-final-get-started">
              <Button variant="outline" className="h-12 px-7 rounded-full text-base border-white/40 text-white bg-transparent hover:bg-white hover:text-[var(--mh-charcoal)] transition-colors">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-20 py-12 bg-[var(--mh-soft-white)] border-t border-[var(--mh-border)]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo theme="black" height={34} />
          <div className="flex items-center gap-5 text-sm text-[var(--mh-muted-text)]">
            <Link to="/terms" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="pricing-footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="pricing-footer-privacy">Privacy Policy</Link>
          </div>
          <p className="text-xs text-[var(--mh-muted-text)]">© {new Date().getFullYear()} MinistryHub. Made for the local church.</p>
        </div>
      </footer>
    </div>
  );
}
